import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

export default function WheelOfTransformation() {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true }); // Lowered threshold for mobile

  const segments = [
    {
      title: 'Vision & Leadership Alignment',
      color: '#1E40AF',
      rotation: 0,
      challenges: ['Unclear vision', 'No 3-4 year strategy', 'Leadership misalignment'],
      interventions: ['Co-create vision', 'Define 8Ps strategy', 'KFAs & operating plans']
    },
    {
      title: 'Organisation Structure',
      color: '#84CC16',
      rotation: 72,
      challenges: ['Spaghetti structures', 'Unclear roles', 'Misalignment with vision'],
      interventions: ['Scalable structures', 'Clear ARA matrix', 'Build/Buy decisions']
    },
    {
      title: 'Organisation Culture',
      color: '#F97316',
      rotation: 144,
      challenges: ['Values in boardroom only', 'Lack of transparency', 'Unfair practices'],
      interventions: ['Define acceptable behaviors', 'Embed values in DNA', 'Break down silos']
    },
    {
      title: 'Competency Framework',
      color: '#EAB308',
      rotation: 216,
      challenges: ['Not backed by science', 'No ownership of success', 'Positions for people'],
      interventions: ['Behavioral frameworks', 'IDPs & assessments', 'My Success = My Responsibility']
    },
    {
      title: 'Employee Engagement',
      color: '#059669',
      rotation: 288,
      challenges: ['Employees not excited to work', 'Lack of WIIFM', 'Engagement as fun & games'],
      interventions: ['Scientific engagement studies', 'Culture of dialogue', 'Meaning + Peak Productivity']
    }
  ];

  return (
    <div ref={ref} className="relative py-32 bg-gradient-to-br from-[#1A1008] to-[#3D1C11] overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#E8642C] rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#E8642C] rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
        {/* Header */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <p className="text-[13px] font-medium text-[#E8642C] tracking-[0.2em] uppercase mb-6">
              Our Proprietary Framework
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight mb-6">
              The Wheel of Transformation
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-white/70 max-w-[800px] mx-auto leading-relaxed">
              A holistic model that combines an understanding of business, management, and family dynamics. 
              Transformation is a journey to achieve high impact both at individual and organizational levels.
            </p>
          </motion.div>
        </div>

        {/* Wheel Visualization */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Animated Wheel */}
          <div className="relative flex items-center justify-center px-4 py-8 lg:py-0">
            <motion.div
              className="relative w-full max-w-[320px] sm:max-w-[380px] lg:max-w-[500px] h-[320px] sm:h-[380px] lg:h-[500px]"
              initial={{ opacity: 0, scale: 0.8, rotate: -180 }}
              animate={inView ? { opacity: 1, scale: 1, rotate: 0 } : {}}
              transition={{ duration: 1.2, ease: "easeOut" }}
            >
              {/* Center ROI Circle */}
              <motion.div
                className="absolute inset-0 flex items-center justify-center"
                initial={{ scale: 0 }}
                animate={inView ? { scale: 1 } : {}}
                transition={{ delay: 0.5, duration: 0.6 }}
              >
                <div className="w-28 h-28 sm:w-32 sm:h-32 lg:w-40 lg:h-40 rounded-full bg-gradient-to-br from-[#E8642C] to-[#CC2929] flex items-center justify-center shadow-2xl border-4 border-white/20">
                  <div className="text-center">
                    <div className="text-white font-bold text-xl sm:text-2xl lg:text-3xl">ROI</div>
                    <div className="text-white/80 text-[10px] sm:text-xs mt-1">Return on<br/>Investment</div>
                  </div>
                </div>
              </motion.div>

              {/* Segments */}
              {segments.map((segment, index) => (
                <motion.div
                  key={index}
                  className="absolute inset-0"
                  style={{ transform: `rotate(${segment.rotation}deg)` }}
                  initial={{ opacity: 0 }}
                  animate={inView ? { opacity: 1 } : {}}
                  transition={{ delay: 0.2 + index * 0.1, duration: 0.6 }}
                >
                  <div className="absolute left-1/2 top-0 -translate-x-1/2">
                    <div 
                      className="w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 rounded-full flex items-center justify-center text-white text-center p-3 sm:p-4 shadow-xl transform -rotate-90"
                      style={{ 
                        background: segment.color,
                        transform: `rotate(-${segment.rotation}deg)`
                      }}
                    >
                      <span className="text-[10px] sm:text-xs font-bold leading-tight">{segment.title}</span>
                    </div>
                  </div>
                </motion.div>
              ))}

              {/* Connecting Lines */}
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 500 500" style={{ transform: 'rotate(-90deg)' }}>
                {segments.map((_, index) => {
                  const angle = (index * 72) * Math.PI / 180;
                  const nextAngle = ((index + 1) * 72) * Math.PI / 180;
                  const radius = 170;
                  const centerX = 250;
                  const centerY = 250;
                  
                  const x1 = centerX + radius * Math.cos(angle);
                  const y1 = centerY + radius * Math.sin(angle);
                  const x2 = centerX + radius * Math.cos(nextAngle);
                  const y2 = centerY + radius * Math.sin(nextAngle);
                  
                  return (
                    <motion.line
                      key={index}
                      x1={x1}
                      y1={y1}
                      x2={x2}
                      y2={y2}
                      stroke="rgba(255,255,255,0.2)"
                      strokeWidth="2"
                      initial={{ pathLength: 0 }}
                      animate={inView ? { pathLength: 1 } : {}}
                      transition={{ delay: 0.8 + index * 0.1, duration: 0.4 }}
                    />
                  );
                })}
              </svg>
            </motion.div>
          </div>

          {/* Segment Details */}
          <div className="space-y-6">
            {segments.map((segment, index) => (
              <motion.div
                key={index}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all"
                initial={{ opacity: 0, x: 50 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.3 + index * 0.1, duration: 0.6 }}
              >
                <div className="flex items-start gap-4">
                  <div 
                    className="flex-shrink-0 w-3 h-3 rounded-full mt-1.5"
                    style={{ background: segment.color }}
                  />
                  <div className="flex-1">
                    <h3 className="text-white font-bold text-lg mb-3">{segment.title}</h3>
                    <div className="grid md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="text-[#E8642C] font-semibold mb-2">Challenges:</div>
                        <ul className="space-y-1 text-white/60">
                          {segment.challenges.map((challenge, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="text-white/40">•</span>
                              <span>{challenge}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <div className="text-[#E8642C] font-semibold mb-2">Interventions:</div>
                        <ul className="space-y-1 text-white/60">
                          {segment.interventions.map((intervention, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="text-[#E8642C]">→</span>
                              <span>{intervention}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="mt-20 text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1, duration: 0.8 }}
        >
          <p className="text-white/70 text-lg mb-6">
            At Anexi Consulting, we've built a framework around people capabilities that enable successful implementation for transformation.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 bg-[#E8642C] text-white px-8 py-4 rounded-xl text-sm font-semibold hover:bg-[#CC2929] transition-all"
          >
            Start Your Transformation Journey
          </a>
        </motion.div>
      </div>
    </div>
  );
}
